import { dateOnlyOf } from '@/lib/utils/custom-date/custom-date';

// Every day here is a plain "YYYY-MM-DD" label (leave and holiday days are date only), so the
// arithmetic is UTC on the label and never goes through a time zone.

const toUtcDate = (day: string) => {
  const [year, month, date] = day.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, date));
};

/** `day` moved by `days` calendar days. */
export const addDays = (day: string, days: number): string => {
  const date = toUtcDate(day);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
};

/** Monday of the week `day` falls in. */
export const weekStartOf = (day: string): string =>
  addDays(day, -((toUtcDate(day).getUTCDay() + 6) % 7));

/** The seven days of the week starting on `weekStart`. */
export const weekDaysFrom = (weekStart: string): string[] =>
  Array.from({ length: 7 }, (_, index) => addDays(weekStart, index));

/** The first day of the month `day` falls in. */
export const monthStartOf = (day: string): string => `${day.slice(0, 7)}-01`;

/** The first day of the month `months` months away from the one `day` falls in. */
export const addMonths = (day: string, months: number): string => {
  const date = toUtcDate(monthStartOf(day));
  date.setUTCMonth(date.getUTCMonth() + months);
  return date.toISOString().slice(0, 10);
};

/**
 * `day` moved by `months` months, keeping its day of the month (clamped to the target month's last
 * day, so 31 Jan + 1 month is 28 or 29 Feb).
 */
export const addMonthsKeepingDay = (day: string, months: number): string => {
  const start = addMonths(day, months);
  const lastDate = Number(addDays(addMonths(start, 1), -1).slice(8));
  const date = Math.min(Number(day.slice(8)), lastDate);
  return `${start.slice(0, 8)}${String(date).padStart(2, '0')}`;
};

/** How many days of the week come before `day` when weeks start on Monday (0 for a Monday). */
export const weekdayIndexOf = (day: string): number => (toUtcDate(day).getUTCDay() + 6) % 7;

/** Whether a leave from `start` to `end` (date only, both inclusive) covers `day`. */
export const coversDay = (start: string, end: string, day: string): boolean => {
  const first = dateOnlyOf(start);
  const last = dateOnlyOf(end) || first;
  return Boolean(first) && day >= first && day <= last;
};

/** Every day of the month `day` falls in. */
export const monthDaysOf = (day: string): string[] => {
  const start = monthStartOf(day);
  const next = addMonths(start, 1);
  const days: string[] = [];
  for (let current = start; current < next; current = addDays(current, 1)) days.push(current);
  return days;
};

/**
 * The first and last index in `days` a leave from `start` to `end` (date only, both inclusive)
 * covers, or `undefined` when it falls outside them. Read by date part, so old rows stored at a
 * non-midnight instant still land on their day.
 */
export const daySpanOf = (
  start: string,
  end: string,
  days: string[]
): { from: number; to: number } | undefined => {
  const first = dateOnlyOf(start);
  const last = dateOnlyOf(end) || first;
  const from = days.findIndex((day) => day >= first && day <= last);
  if (from === -1) return undefined;
  let to = from;
  while (to + 1 < days.length && days[to + 1] <= last) to += 1;
  return { from, to };
};

/** A day label in the user's language (`Mon`, `27 Oct`), formatted in UTC so it never shifts. */
export const formatDayLabel = (
  day: string,
  locale: string,
  options: Intl.DateTimeFormatOptions
): string =>
  new Intl.DateTimeFormat(locale, { ...options, timeZone: 'UTC' }).format(toUtcDate(day));
